import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  claimAutomaticRedirect,
  resetRedirectGuard
} from './login-redirect-guard';
import { loginOptions } from './login-options';
import usePermissionsTokenReady from './usePermissionsTokenReady';

// The portal's login startup, the same flow as the TMS shell's useAuthStartup.

// How long the portal waits for auth0-react's startup silent-authentication check before it
// stops waiting and falls back to an interactive login. A silent-auth request that is rejected
// without a postMessage only ends when the SDK's 60 s iframe timeout fires; that SDK option is
// global and also bounds every silent token renewal, so the portal bounds only its own wait.
export const SILENT_AUTH_STARTUP_LIMIT_MS = 10_000;

// OAuth error codes that mean "the user has to interact with the login page". An interactive
// login resolves them, so the portal redirects. Any other error (access_denied from a post-login
// Action, a failed token exchange, an invalid state, a network or origin failure) would fail
// the same way again, so the portal shows it instead of redirecting into a loop.
const INTERACTIVE_LOGIN_ERRORS = [
  'login_required',
  'consent_required',
  'interaction_required'
];

// auth0-react surfaces OAuth failures as GenericError (@auth0/auth0-spa-js) or OAuthError,
// both of which carry the OAuth error code in `error`. Plain Errors carry none.
const oauthErrorCode = (error: Error): string | undefined => {
  const code = (error as { error?: unknown }).error;
  return typeof code === 'string' ? code : undefined;
};

const requiresInteractiveLogin = (error: Error) =>
  INTERACTIVE_LOGIN_ERRORS.includes(oauthErrorCode(error) ?? '');

// A post-login Action denied this user (for example an unverified email). Logging in again would
// be denied the same way, possibly without the login page if Auth0 kept a session, so the portal
// offers only a logout back to the login page.
const ACCESS_DENIED = 'access_denied';

export type AuthStartupStatus =
  | 'loading'
  | 'authenticated'
  | 'error'
  | 'accessDenied'
  | 'redirectLoop';

export interface AuthStartup {
  status: AuthStartupStatus;
  errorMessage?: string;
  retry: () => void;
  backToLogin: () => void;
}

const useAuthStartup = (): AuthStartup => {
  const { isLoading, user, error, loginWithRedirect, logout } = useAuth0();
  // The first login's token has no permissions yet; the layout calls APIs as soon as it mounts.
  const tokenReady = usePermissionsTokenReady();
  const [silentAuthTimedOut, setSilentAuthTimedOut] = useState(false);
  const [redirectBlocked, setRedirectBlocked] = useState(false);
  const redirectRequested = useRef(false);

  useEffect(() => {
    if (!isLoading) {
      return undefined;
    }
    const timer = setTimeout(
      () => setSilentAuthTimedOut(true),
      SILENT_AUTH_STARTUP_LIMIT_MS
    );
    return () => clearTimeout(timer);
  }, [isLoading]);

  const blockingError =
    error && !requiresInteractiveLogin(error) ? error : undefined;
  const needsLogin =
    (!isLoading || silentAuthTimedOut) && !user && !blockingError;

  useEffect(() => {
    if (user) {
      resetRedirectGuard();
      return;
    }
    if (!needsLogin || redirectRequested.current) {
      return;
    }
    redirectRequested.current = true;
    if (claimAutomaticRedirect()) {
      void loginWithRedirect(loginOptions());
    } else {
      setRedirectBlocked(true);
    }
  }, [user, needsLogin, loginWithRedirect]);

  const retry = () => {
    resetRedirectGuard();
    void loginWithRedirect(loginOptions());
  };

  const backToLogin = () => {
    resetRedirectGuard();
    void logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (blockingError) {
    return {
      status:
        oauthErrorCode(blockingError) === ACCESS_DENIED
          ? 'accessDenied'
          : 'error',
      errorMessage: blockingError.message,
      retry,
      backToLogin
    };
  }
  if (redirectBlocked) {
    return { status: 'redirectLoop', retry, backToLogin };
  }
  return {
    status: user && tokenReady ? 'authenticated' : 'loading',
    retry,
    backToLogin
  };
};

export default useAuthStartup;
